import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { DicomColor } from '~/domain/dicom/dicomColor';
import {
    RoiGenerationAlgorithmType,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { Environment } from '~/engine/data/Environment';
import useStructureSet from './useStructureSet';
import { getPreviewColor } from './previewColor';
import { domainEventBus, RoiColorChangeRequestedEvent } from '~/domain/events';
import { editBlockedMessageId } from '~/components/Structures/editBlockedMessage';
import type { RoiId } from '~/domain/changes/ids';

interface UseGetOrCreateDestinationOptions {
    environment: Environment | null;
}

/** Typing/provenance applied when the destination is newly created. */
export interface CreateDestinationOptions {
    interpretedType?: RoiInterpretedType;
    generation?: {
        algorithm: RoiGenerationAlgorithmType;
        description?: string;
    };
    identificationCode?: RoiIdentificationCodeSequence;
    /** Color for the new structure; the operation green when omitted. */
    color?: DicomColor;
}

/**
 * Shared hook that either looks up an existing output ROI (by its id) or
 * creates a new one (with the given name), applying a derived preview color to
 * newly-created structures so the rendering engine and domain model stay in
 * sync.
 */
export function useGetOrCreateDestination({
    environment,
}: UseGetOrCreateDestinationOptions) {
    const intl = useIntl();
    const { createNewRoi } = useStructureSet();

    const getOrCreateDestination = useCallback(
        async (
            outputRoiId: RoiId | null,
            newName?: string,
            createOptions?: CreateDestinationOptions
        ) => {
            const { structureSet } = environment || {};
            if (!structureSet) {
                return null;
            }

            if (outputRoiId === null) {
                if (!newName) {
                    throw new Error(
                        'newName is required when creating a new ROI'
                    );
                }
                const newRoi = await createNewRoi(
                    structureSet.sopInstanceUID,
                    newName,
                    createOptions?.interpretedType,
                    true,
                    {
                        generation: createOptions?.generation,
                        identificationCode: createOptions?.identificationCode,
                    }
                );
                if (!newRoi) {
                    enqueueSnackbar('Failed to create new ROI', {
                        variant: 'error',
                    });
                    return null;
                }
                const dest = structureSet.getRoi(newRoi.id);
                if (!dest) {
                    enqueueSnackbar(`Structure "${newName}" not found`, {
                        variant: 'error',
                    });
                    return null;
                }

                // Set the new structure's color via the domain event so
                // both the domain model (Recoil/structure panel) and the
                // rendering engine (RegionOfInterest) stay in sync. An
                // explicit color is a user choice (source 'user'); the
                // fixed operation green fallback is system-initiated.
                const previewCol = getPreviewColor([0, 0, 0]);
                const newColor = createOptions?.color ?? {
                    R: Math.round(previewCol[0] * 255),
                    G: Math.round(previewCol[1] * 255),
                    B: Math.round(previewCol[2] * 255),
                };
                await domainEventBus.publish(
                    new RoiColorChangeRequestedEvent(
                        dest.roiId,
                        newColor,
                        createOptions?.color ? 'user' : 'system'
                    )
                );

                return dest;
            }

            const dest = structureSet.getRoi(outputRoiId);
            if (!dest) {
                enqueueSnackbar('Target structure not found', {
                    variant: 'error',
                });
                return null;
            }

            const blocker = dest.contourEditBlocker;
            if (blocker) {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: editBlockedMessageId(blocker),
                    }),
                    { variant: 'error' }
                );
                return null;
            }

            return dest;
        },
        [environment, createNewRoi, intl]
    );

    return { getOrCreateDestination };
}
